import * as _ from 'lodash';
import * as hash from 'object-hash';
import * as moment from 'moment';
import { MutableRefObject, useContext, useEffect, useReducer } from 'react';
import { connect, Dispatch } from 'react-redux';
import { IApplicationState, AppActionCreators, RecipientActionCreators, IAppState, IRecipientLookupResponse, FileActionCreators, IRecipientPostTransplantResponse, IRecipientHeaderInfoResponse } from '@nkr/store/index';
import { IBaseResponse, IPostTransplantRejection, IRecipientProfilePostTransplantData, IRecipientProfilePostTransplantDataRequest } from '@nkr/models/index';
import { IPostTransplantRejectionEpisodeResponse } from '@nkr/store/recipient/actions-recipient-profile';
import { FileDocumentType, FileDocumentTypeDescription } from '@nkr/enums/index';
import { toast } from 'react-toastify';
import { downloadFile } from '@nkr/services/download-file';
import { AfterAction, RecipientProfileContext, ToggleType } from '../models';
import { ProfileWrapper } from '../styles';
import { IValidation } from './validation';
import { Utility } from '@nkr/services/utility';
import { initialState, initialValidation, postTransplantReducer } from './state';
import Content from './content';

interface IProps {
    saveRef: MutableRefObject<any>
}

interface IStateProps {
    app: IAppState
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    downloadFile: (docType: number, lookupId: number) => Promise<any>,
    getRecipientLookupData: (centerId: number, isAdmin: boolean) => Promise<IRecipientLookupResponse>,
    getPostTransplant: (centerId: number, recipientId: number) => Promise<IRecipientPostTransplantResponse>,
    updatePostTransplant: (centerId: number, recipientId: number, recipientPostTransplant: IRecipientProfilePostTransplantDataRequest) => Promise<IBaseResponse>,
    getFinancialInfo: (centerId: number, donorId: number, recipientId: number) => Promise<any>,
    getRejectionEpisodes: (isAdmin: boolean, centerId: number, recipientId: number) => Promise<IPostTransplantRejectionEpisodeResponse>,
    saveRejectionEpisodeDraft: (centerId: number, recipientId: number, rejection: IPostTransplantRejection) => Promise<IBaseResponse>,
    createRejectionEpisode: (centerId: number, recipientId: number, rejection: IPostTransplantRejection) => Promise<IBaseResponse>,
    updateRejectionEpisode: (centerId: number, recipientId: number, rejection: IPostTransplantRejection) => Promise<IBaseResponse>,
    deleteRejectionEpisode: (centerId: number, recipientId: number, rejectionId: number) => Promise<IBaseResponse>,
    unDeleteRejectionEpisode: (centerId: number, recipientId: number, rejectionId: number) => Promise<IBaseResponse>,
}

type TProps = IProps & IStateProps & IDispatchProps;

const PostTransplant = (props: TProps) => {
    const context = useContext(RecipientProfileContext);
    const [state, dispatch] = useReducer(postTransplantReducer, initialState);

    useEffect(() => {
        getData();
    }, []);

    const getData = async () => {
        props.showLoading("Getting Post Transplant, one moment please....").then(() => {
            Promise.all([
                props.getRecipientLookupData(context.centerId, context.isAdmin),
                props.getPostTransplant(context.centerId, context.recipientId),
                props.getRejectionEpisodes(context.isAdmin, context.centerId, context.recipientId)
            ])
                .then(([lookupResponse, dataResponse, rejectionEpisodeDataResponse]) => {
                    if (lookupResponse.data.success && dataResponse.data.success && rejectionEpisodeDataResponse.data.success) {
                        let data = dataResponse.data.results;

                        let rejectionEpisodeList = rejectionEpisodeDataResponse.data.results

                        let {
                            swapList, logisticsList, desensitizationList, donorRelationList, transportMethodList,
                            acuteAMRGradeList, acuteTCMRGradeList, biopsyReasonList, iftaGradeList, rejectionDSATypeList,
                            rejectionTreatmentTypeList, graftFailureReasonList
                        } = lookupResponse.data.results;

                        dispatch({
                            type: 'afterLoad',
                            payload: {
                                data,
                                rejectionEpisodeList,
                                hash: hash(data),
                                lookupData: {
                                    swapList,
                                    logisticsList,
                                    desensitizationList,
                                    donorRelationList,
                                    transportMethodList,
                                    acuteAMRGradeList,
                                    acuteTCMRGradeList,
                                    biopsyReasonList,
                                    iftaGradeList,
                                    rejectionDSATypeList,
                                    rejectionTreatmentTypeList,
                                    graftFailureReasonList
                                }
                            }
                        });
                    }
                }).catch(() => {

                }).finally(() => {
                    props.hideLoading();
                });
        });
    };

    const save = () => {
        let {
            wasDesensitized,
            desensitizationType,
            primaryNonFunction,
            donatedBy,
            transportationCost,
            shippedMethod,
            isKflMatch,
            isWillingToMeetDonor,
            isSharingGfAndCreatinine,
            unosCPRA,
            exportableDaysInNkr,
            wasPreviouslySensitized,
            deathDate,
            isAlive,
            primaryCauseOfDeath,
            secondaryCauseOfDeath,
            rejectionEpisodes,
            rejectionEpisodeDetail,
            lostToFollowup,
            admitCreatinine,
            admitCreatinineDate,
            creatinineWeek,
            creatinineWeekDate,
            creatinineMonth,
            creatinineMonthDate,
            creatinine6Month,
            creatinine6MonthDate,
            creatinineYear,
            creatinineYearDate,
            creatinineYear2,
            creatinineYear2Date,
            creatinineYear3,
            creatinineYear3Date,
            creatinineYear4,
            creatinineYear4Date,
            creatinineYear5,
            creatinineYear5Date,
            creatinineYear6,
            creatinineYear6Date,
            creatinineYear7,
            creatinineYear7Date,
            creatinineYear8,
            creatinineYear8Date,
            creatinineYear9,
            creatinineYear9Date,
            creatinineYear10,
            creatinineYear10Date,
            hadDelayedGraftFunction,
            graftSurvival,
            graftFailureDate,
            graftFailureReason,
            graftFailureReasonOther,
            rejectionDueToNonCompliance,
            donorsUnosId,
            desensitization,
            swapType,
            courierJobNumber,
            kflFollowupOptOut,
            hasDeNovoDSA,
            deNovoDSADate,
            deNovoDSA,
            deNovoDSAMFI,
            coldIschemicHours,
            coldIschemicMinutes,
            packagingRemovalDateTime,
            reperfusionDateTime,
            immediateKidneyFunction,
            transplantDate
        } = state.data;

        let data: IRecipientProfilePostTransplantDataRequest = {
            shippedMethod,
            courierJobNumber,
            isKflMatch,
            transportationCost: parseFloat(transportationCost),

            isWillingToMeetDonor,
            isSharingGfAndCreatinine,

            kflFollowupOptOut,
            hasDeNovoDSA,
            deNovoDSADate: deNovoDSADate != '' ? moment(deNovoDSADate).format('YYYY-MM-DD') : null,
            deNovoDSA,
            deNovoDSAMFI,

            unosCPRA,
            exportableDaysInNkr,
            wasPreviouslySensitized,
            coldIschemicHours,
            coldIschemicMinutes,
            donorsUnosId,

            graftSurvival,
            graftFailureDate: graftFailureDate != '' ? moment(graftFailureDate).format('YYYY-MM-DD') : null,
            graftFailureReason,
            graftFailureReasonOther,
            rejectionDueToNonCompliance: rejectionDueToNonCompliance == 1
                ? true :
                rejectionDueToNonCompliance == 0
                    ? false : null,
            hadDelayedGraftFunction,
            primaryNonFunction,

            donatedBy,
            isAlive,
            deathDate: deathDate != '' ? moment(deathDate).format('YYYY-MM-DD') : null,
            primaryCauseOfDeath,
            secondaryCauseOfDeath,
            rejectionEpisodes: rejectionEpisodes != -1 ? rejectionEpisodes === 1 : null,
            rejectionEpisodeDetail,
            lostToFollowup,

            wasDesensitized,
            desensitizationType,

            desensitization1: desensitization[0],
            desensitization2: desensitization[1],
            desensitization3: desensitization[2],

            admitCreatinine,
            admitCreatinineDate: admitCreatinineDate != '' ? moment(admitCreatinineDate).format('YYYY-MM-DD') : null,
            creatinineWeek,
            creatinineWeekDate: creatinineWeekDate != '' ? moment(creatinineWeekDate).format('YYYY-MM-DD') : null,
            creatinineMonth,
            creatinineMonthDate: creatinineMonthDate != '' ? moment(creatinineMonthDate).format('YYYY-MM-DD') : null,
            creatinine6Month,
            creatinine6MonthDate: creatinine6MonthDate != '' ? moment(creatinine6MonthDate).format('YYYY-MM-DD') : null,
            creatinineYear,
            creatinineYearDate: creatinineYearDate != '' ? moment(creatinineYearDate).format('YYYY-MM-DD') : null,
            creatinineYear2,
            creatinineYear2Date: creatinineYear2Date != '' ? moment(creatinineYear2Date).format('YYYY-MM-DD') : null,
            creatinineYear3,
            creatinineYear3Date: creatinineYear3Date != '' ? moment(creatinineYear3Date).format('YYYY-MM-DD') : null,
            creatinineYear4,
            creatinineYear4Date: creatinineYear4Date != '' ? moment(creatinineYear4Date).format('YYYY-MM-DD') : null,
            creatinineYear5,
            creatinineYear5Date: creatinineYear5Date != '' ? moment(creatinineYear5Date).format('YYYY-MM-DD') : null,
            creatinineYear6,
            creatinineYear6Date: creatinineYear6Date != '' ? moment(creatinineYear6Date).format('YYYY-MM-DD') : null,
            creatinineYear7,
            creatinineYear7Date: creatinineYear7Date != '' ? moment(creatinineYear7Date).format('YYYY-MM-DD') : null,
            creatinineYear8,
            creatinineYear8Date: creatinineYear8Date != '' ? moment(creatinineYear8Date).format('YYYY-MM-DD') : null,
            creatinineYear9,
            creatinineYear9Date: creatinineYear9Date != '' ? moment(creatinineYear9Date).format('YYYY-MM-DD') : null,
            creatinineYear10,
            creatinineYear10Date: creatinineYear10Date != '' ? moment(creatinineYear10Date).format('YYYY-MM-DD') : null,

            swapType,

            packagingRemovalDateTime,
            reperfusionDateTime,
            immediateKidneyFunction,

            transplantDate
        };

        props.showLoading('Saving Post Transplant data, one moment please...').then(() => {
            props.updatePostTransplant(context.centerId, context.recipientId, data)
                .then(response => {
                    if (response.data.success) {
                        dispatch({
                            type: 'updateHash',
                            payload: hash(state.data)
                        });

                        context.updateHasChanges(false);

                        toast.success('Post transplant data updated successfully');
                    }
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const saveRejectionEpisodeDraft = (rejection: IPostTransplantRejection) => {
        props.showLoading('Saving Rejection Episode Draft, one moment please...').then(() => {
            props.saveRejectionEpisodeDraft(context.centerId, context.recipientId, rejection).then((response: any) => {
                if (response.data.success) {
                    getData();
                }
            }).catch(() => {
                props.hideLoading();
            });
        });
    }

    const createRejectionEpisode = (rejection: IPostTransplantRejection) => {
        props.showLoading('Creating Rejection Episode, one moment please...').then(() => {
            props.createRejectionEpisode(context.centerId, context.recipientId, rejection).then((response: any) => {
                if (response.data.success) {
                    getData();
                }
            }).catch(() => {
                props.hideLoading();
            });
        });
    }

    const updateRejectionEpisode = (rejection: IPostTransplantRejection) => {
        props.showLoading('Updating Rejection Episode, one moment please...').then(() => {
            props.updateRejectionEpisode(context.centerId, context.recipientId, rejection).then((response: any) => {
                if (response.data.success) {
                    getData();
                }
            }).catch(() => {
                props.hideLoading();
            });
        });
    }

    const deleteRejectionEpisode = (rejectionId: number) => {
        props.showLoading('Deleting Rejection Episode, one moment please...').then(() => {
            props.deleteRejectionEpisode(context.centerId, context.recipientId, rejectionId).then((response: any) => {
                if (response.data.success) {
                    getData();
                }
            }).catch(() => {
                props.hideLoading();
            });
        });
    }

    const unDeleteRejectionEpisode = (rejectionId: number) => {
        props.showLoading('Restoring Rejection Episode, one moment please...').then(() => {
            props.unDeleteRejectionEpisode(context.centerId, context.recipientId, rejectionId).then((response: any) => {
                if (response.data.success) {
                    getData();
                }
            }).catch(() => {
                props.hideLoading();
            });
        });
    }

    const handleGetFinancialInfo = (donorId: number) => {
        props.showLoading('Getting financial info, one moment please...').then(() => {
            props.getFinancialInfo(context.centerId, donorId, context.recipientId).then((response: any) => {
                props.hideLoading();

                downloadFile(response.data, `financial-info-${context.centerId}-${donorId}-${context.recipientId}.pdf`, 'application/pdf');
            });
        });
    };

    const handleDataChange = (data: IRecipientProfilePostTransplantData) => {
        let newHash = hash(data);

        var hasChanged = state.hash != newHash;

        dispatch({
            type: 'updateData',
            payload: data
        });

        context.updateHasChanges(hasChanged);
    };

    const handleViewFile = (docType: FileDocumentType) => {
        props.downloadFile(docType, state.data.transplantedDonor.id).then((response: any) => {
            downloadFile(response.data, `${FileDocumentTypeDescription[docType]}.pdf`, response.headers.contentType)
        }).catch(() => {
            toast.error("Unable to download file at this time.  Please contact support.");
        });
    };

    const handleValidate = (afterAction: AfterAction) => {
        let {
            transplantDate,
            transportationCost,
            unosCPRA,
            wasPreviouslySensitized,
            coldIschemicHours,
            graftSurvival,
            graftFailureDate,
            graftFailureReason,
            graftFailureReasonOther,
            rejectionDueToNonCompliance,
            hadDelayedGraftFunction,
            isAlive,
            deathDate,
            primaryCauseOfDeath,
            rejectionEpisodes,
            rejectionEpisodeDetail,
            primaryNonFunction,
            wasDesensitized,
            desensitizationType,
            admitCreatinine,
            admitCreatinineDate,
            creatinineWeek, creatinineWeekDate,
            creatinineMonth, creatinineMonthDate,
            creatinine6Month, creatinine6MonthDate,
            creatinineYear, creatinineYearDate,
            creatinineYear2, creatinineYear2Date,
            creatinineYear3, creatinineYear3Date,
            creatinineYear4, creatinineYear4Date,
            creatinineYear5, creatinineYear5Date,
            creatinineYear6, creatinineYear6Date,
            creatinineYear7, creatinineYear7Date,
            creatinineYear8, creatinineYear8Date,
            creatinineYear9, creatinineYear9Date,
            creatinineYear10, creatinineYear10Date
        } = state.data;

        let list = [
            { value: admitCreatinine, date: admitCreatinineDate },
            { value: creatinineWeek, date: creatinineWeekDate },
            { value: creatinineMonth, date: creatinineMonthDate },
            { value: creatinine6Month, date: creatinine6MonthDate },
            { value: creatinineYear, date: creatinineYearDate },
            { value: creatinineYear2, date: creatinineYear2Date }, { value: creatinineYear3, date: creatinineYear3Date }, { value: creatinineYear4, date: creatinineYear4Date },
            { value: creatinineYear5, date: creatinineYear5Date }, { value: creatinineYear6, date: creatinineYear6Date }, { value: creatinineYear7, date: creatinineYear7Date },
            { value: creatinineYear8, date: creatinineYear8Date }, { value: creatinineYear9, date: creatinineYear9Date },
            { value: creatinineYear10, date: creatinineYear10Date }
        ];

        let transplantDateValue = moment(transplantDate);

        let validTransplantDate = transplantDateValue.isValid() && transplantDateValue.isSameOrBefore(moment());
        let isFourDaysPastTransplant = validTransplantDate && transplantDateValue.isBefore(moment().subtract(4, 'days'));
        let isOneMonthAnd30DaysPastTransplant = validTransplantDate && transplantDateValue.isBefore(moment().subtract(1, 'month').subtract(30, 'days'));

        let creatinine: { value: boolean, date: boolean }[] = [];

        for (let n = 0; n < list.length; n++) {
            let item = list[n];

            let value = item.value != '' ? Utility.isNumber(item.value) : true;
            let date = item.date != '' ? moment(item.date).isValid() : true;

            if (isFourDaysPastTransplant && n == 0) {
                value = Utility.isNumber(item.value);
                date = moment(item.date).isValid();
            }

            creatinine.push({ value, date });
        }

        let shortValidation = state.data.lostToFollowup || !isAlive || graftSurvival == false;

        let graftFailureDateValue = moment(graftFailureDate);

        let validation: IValidation = {
            ...initialValidation,
            transplantDate: validTransplantDate,
            transportCost: Utility.isNumber(transportationCost),

            graftSurvival: isFourDaysPastTransplant ? graftSurvival != null : true,

            graftFailureDate: graftSurvival == false ? graftFailureDateValue.isValid() && graftFailureDateValue.isSameOrAfter(transplantDateValue) : true,
            graftFailureReason: graftSurvival == false ? !!graftFailureReason : true,
            graftFailureReasonOther: graftSurvival == false && graftFailureReason == 'Other' ? graftFailureReasonOther != '' && graftFailureReasonOther != null && graftFailureReasonOther != undefined : true,
            rejectionDueToNonCompliance: graftSurvival == false && graftFailureReason == 'Rejection' ? rejectionDueToNonCompliance != null : true,

            deathDate: isAlive == false ? moment(deathDate).isValid() : true,
            primaryCauseOfDeath: isAlive == false ? !!primaryCauseOfDeath : true,
            rejectionEpisodeDetail: isAlive == false && rejectionEpisodes ? !!rejectionEpisodeDetail : true,
        };

        if (!shortValidation) {
            validation = {
                ...validation,
                wasPreviouslySensitized: isFourDaysPastTransplant ? wasPreviouslySensitized != null : true,
                coldIschemicHours: isFourDaysPastTransplant ? coldIschemicHours != -1 : true,
                hadDelayedGraftFunction: isFourDaysPastTransplant ? hadDelayedGraftFunction != null : true,
                donorUnosId: state.data.transplantedDonor != null ? !!state.data.donorsUnosId : true,
                wasDesensitized: isFourDaysPastTransplant ? wasDesensitized != null : true,
                desensitizationType: isFourDaysPastTransplant && wasDesensitized == true ? !!desensitizationType : true,
                unosCPRA: isFourDaysPastTransplant ? !!unosCPRA : true,
                primaryNonFunction: isOneMonthAnd30DaysPastTransplant ? primaryNonFunction != null : true,
                adminCreatinine: isFourDaysPastTransplant ? creatinine[0].value == true && creatinine[0].date == true : true,
                creatinine
            };
        }

        let validCreatinineDates = validation.creatinine.filter((x, index) => index > 0 && (x.value == false || x.date == false)).length == 0;

        let isValid =
            validation.transplantDate
            && validation.transportCost
            && validation.graftFailureDate
            && validation.graftFailureReason
            && validation.graftFailureReasonOther
            && validation.rejectionDueToNonCompliance
            && validation.deathDate
            && validCreatinineDates;

        let messages = [];

        if (validation.transplantDate == false) {
            messages.push('Invalid transplant date specified');
        }

        if (validation.transportCost == false) {
            messages.push('Invalid transplant cost specified');
        }

        if (validation.unosCPRA == false) {
            messages.push('Please indicate the UNOS CPRA');
        }

        if (validation.coldIschemicHours == false) {
            messages.push('Please enter the cold ischemic hours');
        }

        if (validation.graftSurvival == false) {
            messages.push('Please indicate whether the graft is still functioning');
        }

        if (validation.graftFailureDate == false) {
            messages.push('Please enter the graft failure date; graft failure date must be on or after transplant date');
        }

        if (validation.graftFailureReason == false) {
            messages.push('Please enter the graft failure reason');
        }

        if (validation.graftFailureReasonOther == false) {
            messages.push('Please enter the graft failure reason other details');
        }

        if (validation.rejectionDueToNonCompliance == false) {
            messages.push('Please select if the rejection was due to non compliance');
        }

        if (validation.hadDelayedGraftFunction == false) {
            messages.push('Please specify if there was delayed graft function');
        }

        if (validation.deathDate == false) {
            messages.push('Please enter the patient date of death');
        }

        if (validation.primaryCauseOfDeath == false) {
            messages.push('Please enter the primary cause of death');
        }

        if (validation.rejectionEpisodeDetail == false) {
            messages.push('Please enter the rejection details');
        }

        if (validation.donorUnosId == false) {
            messages.push('Please indicate the UNOS donor ID for the kidney you received for this patient');
        }

        if (validation.desensitizationType == false) {
            messages.push('Please indicate the type of desensitization used');
        }

        if (validation.primaryNonFunction == false) {
            messages.push('Please indicate whether or not the graft was primary non-function');
        }

        if (validation.adminCreatinine == false) {
            messages.push('Please enter creatinine and date at admission');
        }

        if (validation.wasDesensitized == false) {
            messages.push('Please indicate whether or not the patient was desensitized');
        }

        if (validation.wasPreviouslySensitized == false) {
            messages.push('Please indicate whether or not the patient was previously sensitized');
        }

        if (validCreatinineDates == false) {
            messages.push('One or more creatinine dates are invalid');
        }

        let toggleType = ToggleType.NoMessage;

        if (!isValid) {
            toggleType = ToggleType.Errors;
        }

        if (isValid && messages.length > 0) {
            toggleType = ToggleType.Warnings;
        }

        dispatch({
            type: 'updateValidation',
            payload: validation
        });

        context.updateErrors(toggleType, messages);

        if (isValid) {
            save();
        }
    };

    context.registerSave(props.saveRef, handleValidate);

    if (state.data == null)
        return null;

    return (
        <ProfileWrapper>
            <Content
                centerId={context.centerId}
                recipientId={context.recipientId}
                lookupData={state.lookupData}
                data={state.data}
                rejectionEpisodeList={state.rejectionEpisodeList}
                isAdmin={context.isAdmin}
                validation={state.validation}
                onGetFinancialInfo={handleGetFinancialInfo}
                onChange={handleDataChange}
                onViewFile={handleViewFile}
                onSaveRejectionEpisodeDraft={saveRejectionEpisodeDraft}
                onCreateRejectionEpisode={createRejectionEpisode}
                onUpdateRejectionEpisode={updateRejectionEpisode}
                onDeleteRejectionEpisode={deleteRejectionEpisode}
                onUnDeleteRejectionEpisode={unDeleteRejectionEpisode}
            />
        </ProfileWrapper>
    );
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getRecipientLookupData: (centerId, isAdmin) => {
            return dispatch(RecipientActionCreators.getRecipientLookup(centerId, isAdmin));
        },
        getPostTransplant: (centerId, recipientId) => {
            return dispatch(RecipientActionCreators.getPostTransplant(centerId, recipientId))
        },
        updatePostTransplant: (centerId, recipientId, postTransplantInfo) => {
            return dispatch(RecipientActionCreators.updatePostTransplant(centerId, recipientId, postTransplantInfo));
        },
        getFinancialInfo: (centerId, donorId, recipientId) => {
            return dispatch(FileActionCreators.getFinancialAgreement(centerId, donorId, recipientId));
        },
        downloadFile: (docType, id) => {
            return dispatch(FileActionCreators.downloadFile(docType, id))
        },
        getRejectionEpisodes: (isAdmin, centerId, recipientId) => {
            return dispatch(RecipientActionCreators.getRejectionEpisodes(isAdmin, centerId, recipientId))
        },
        saveRejectionEpisodeDraft: (centerId, recipientId, rejection) => {
            return dispatch(RecipientActionCreators.saveRejectionEpisodeDraft(centerId, recipientId, rejection))
        },
        createRejectionEpisode: (centerId, recipientId, rejection) => {
            return dispatch(RecipientActionCreators.createRejectionEpisode(centerId, recipientId, rejection))
        },
        updateRejectionEpisode: (centerId, recipientId, rejection) => {
            return dispatch(RecipientActionCreators.updateRejectionEpisode(centerId, recipientId, rejection))
        },
        deleteRejectionEpisode: (centerId, recipientId, rejectionId) => {
            return dispatch(RecipientActionCreators.deleteRejectionEpisode(centerId, recipientId, rejectionId))
        },
        unDeleteRejectionEpisode: (centerId, recipientId, rejectionId) => {
            return dispatch(RecipientActionCreators.unDeleteRejectionEpisode(centerId, recipientId, rejectionId))
        }
    }
}

export default connect<IStateProps, {}, IProps>(
    mapStateToProps,
    mapDispatchToProps)(PostTransplant);


